import type { GpsCoordinate } from "./GpsTriggerGeometry";

export type GpsTriggerPolygon = { vertices: GpsCoordinate[] };
export type GpsTriggerAudio = { id: string; url: string };
export type GpsTriggerItem = {
    id: string;
    basePoiId: string | null;
    name: string;
    layer: number;
    detailUrl: string | null;
    imageUrl: string | null;
    audio: GpsTriggerAudio | null;
    isSubscriberOnly: boolean;
    isNavigation: boolean;
    zones: GpsTriggerPolygon[];
};
export type GpsTriggerContext = {
    contextKey: string;
    mode: "destination" | "tour";
    destinationId: string;
    language: string;
    contentVersion: number;
    tourId: string | null;
    tourType: string | null;
    tourVersion: number | null;
    items: GpsTriggerItem[];
};

export function parseGpsTriggerContext(value: unknown): GpsTriggerContext | undefined {
    if (!isObject(value) ||
        !isNonEmptyString(value.contextKey) ||
        (value.mode !== "destination" && value.mode !== "tour") ||
        !isNonEmptyString(value.destinationId) ||
        !isNonEmptyString(value.language) ||
        !Number.isFinite(value.contentVersion) ||
        !Array.isArray(value.items)) {
        return undefined;
    }

    const tourId = nullableString(value.tourId);
    const tourType = nullableString(value.tourType);
    const tourVersion = nullableFiniteNumber(value.tourVersion);
    if (value.mode === "tour" && !isNonEmptyString(tourId)) {
        return undefined;
    }

    return {
        contextKey: value.contextKey,
        mode: value.mode,
        destinationId: value.destinationId,
        language: value.language,
        contentVersion: Number(value.contentVersion),
        tourId,
        tourType,
        tourVersion,
        items: value.items.map(parseItem).filter((item): item is GpsTriggerItem => item !== undefined)
    };
}

function parseItem(value: unknown): GpsTriggerItem | undefined {
    if (!isObject(value) ||
        !isNonEmptyString(value.id) ||
        !isNonEmptyString(value.name) ||
        !Number.isFinite(value.layer) ||
        typeof value.isSubscriberOnly !== "boolean" ||
        typeof value.isNavigation !== "boolean" ||
        !Array.isArray(value.zones)) {
        return undefined;
    }

    const detailUrl = value.isNavigation ? null : nullableString(value.detailUrl);
    if (!value.isNavigation && !isNonEmptyString(detailUrl)) {
        return undefined;
    }

    const zones = value.zones.map(parsePolygon).filter((zone): zone is GpsTriggerPolygon => zone !== undefined);
    if (zones.length === 0) {
        return undefined;
    }

    return {
        id: value.id,
        basePoiId: typeof value.basePoiId === "string" ? value.basePoiId.trim() || null : null,
        name: value.name,
        layer: Number(value.layer),
        detailUrl,
        imageUrl: nullableString(value.imageUrl),
        audio: parseAudio(value.audio),
        isSubscriberOnly: value.isSubscriberOnly,
        isNavigation: value.isNavigation,
        zones
    };
}

function parsePolygon(value: unknown): GpsTriggerPolygon | undefined {
    if (!isObject(value) || !Array.isArray(value.vertices) || value.vertices.length < 3) {
        return undefined;
    }

    const vertices = value.vertices.map(parseCoordinate);
    return vertices.every((vertex): vertex is GpsCoordinate => vertex !== undefined)
        ? { vertices }
        : undefined;
}

function parseCoordinate(value: unknown): GpsCoordinate | undefined {
    if (!isObject(value) || !Number.isFinite(value.latitude) || !Number.isFinite(value.longitude)) {
        return undefined;
    }

    const latitude = Number(value.latitude);
    const longitude = Number(value.longitude);
    return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
        ? { latitude, longitude }
        : undefined;
}

function parseAudio(value: unknown): GpsTriggerAudio | null {
    return isObject(value) && typeof value.id === "string" && isNonEmptyString(value.url)
        ? { id: value.id, url: value.url }
        : null;
}

function nullableString(value: unknown): string | null {
    return typeof value === "string" ? value : null;
}

function nullableFiniteNumber(value: unknown): number | null {
    return Number.isFinite(value) ? Number(value) : null;
}

function isNonEmptyString(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0;
}

function isObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object";
}
